import React, { Component } from 'react';
import type { TFunction } from 'i18next';
import { withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import ScrollableAnchor from 'react-scrollable-anchor';
import { bindActionCreators, Dispatch } from 'redux';
import { createSelector } from 'reselect';

import { SuperBlocks } from '../../../../../shared/config/curriculum';
import envData from '../../../../config/env.json';
import { isAuditedSuperBlock } from '../../../../../shared/utils/is-audited';
import Caret from '../../../assets/icons/caret';
import DropDown from '../../../assets/icons/dropdown';
import GreenNotCompleted from '../../../assets/icons/green-not-completed';
import GreenPass from '../../../assets/icons/green-pass';
import { ProgressBar } from '../../../components/Progress/progress-bar';
import { Link } from '../../../components/helpers';
import { completedChallengesSelector } from '../../../redux/selectors';
import { ChallengeNode, CompletedChallenge } from '../../../redux/prop-types';
import { playTone } from '../../../utils/tone';
import { makeExpandedBlockSelector, toggleBlock } from '../redux';
import { isGridBased, isProjectBased } from '../../../utils/curriculum-layout';
import Challenges from './challenges';

import '../intro.css';
import './block-timeline.css';

const { curriculumLocale, showUpcomingChanges, showNewCurriculum } = envData;

type Challenge = ChallengeNode['challenge'];

const mapStateToProps = (state: unknown, ownProps: { block: string }) => {
  const expandedSelector = makeExpandedBlockSelector(ownProps.block);

  return createSelector(
    expandedSelector,
    completedChallengesSelector,
    (isExpanded: boolean, completedChallenges: CompletedChallenge[]) => ({
      isExpanded,
      completedChallengeIds: completedChallenges.map(({ id }) => id)
    })
  )(state as Record<string, unknown>);
};

const mapDispatchToProps = (dispatch: Dispatch) =>
  bindActionCreators({ toggleBlock }, dispatch);

interface BlockProps {
  block: string;
  challenges: Challenge[];
  completedChallengeIds: string[];
  isExpanded: boolean;
  superBlock: SuperBlocks;
  t: TFunction;
  toggleBlock: typeof toggleBlock;
}

export const BlockIntros = ({ intros }: { intros: string[] }): JSX.Element => {
  return (
    <div className='block-description'>
      {intros.map((title, i) => (
        <p dangerouslySetInnerHTML={{ __html: title }} key={i} />
      ))}
    </div>
  );
};

/* ---- Inline SVG icons ---- */

const ChevronIcon = () => (
  <svg viewBox='0 0 16 16' xmlns='http://www.w3.org/2000/svg'>
    <path d='M6 3l5 5-5 5' fill='none' stroke='#6C3BFF' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
  </svg>
);

const CheckIcon = ({ stroke = '#fff', size = 14 }: { stroke?: string; size?: number }) => (
  <svg width={size} height={size} viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <path d='M3 8.5l3.5 3.5L13 4' stroke={stroke} strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round' />
  </svg>
);

class Block extends Component<BlockProps> {
  static displayName = 'Block';

  constructor(props: BlockProps) {
    super(props);
    this.handleBlockClick = this.handleBlockClick.bind(this);
  }

  handleBlockClick(): void {
    const { block, toggleBlock } = this.props;
    void playTone('block-toggle');
    toggleBlock(block);
  }

  render(): JSX.Element {
    const {
      block,
      completedChallengeIds,
      challenges,
      isExpanded,
      superBlock,
      t
    } = this.props;

    let completedCount = 0;

    const challengesWithCompleted = challenges.map(challenge => {
      const isCompleted = completedChallengeIds.includes(challenge.id);
      if (isCompleted) completedCount++;
      return { ...challenge, isCompleted };
    });

    const isProjectBlock = challenges.some(ch =>
      isProjectBased(ch.challengeType, block)
    );
    const isGridBlock = challenges.some(ch =>
      isGridBased(superBlock, ch.challengeType)
    );

    const isAudited = isAuditedSuperBlock(curriculumLocale, superBlock, {
      showNewCurriculum,
      showUpcomingChanges
    });

    const blockTitle = t(`intro:${superBlock}.blocks.${block}.title`);
    const blockIntro = t(`intro:${superBlock}.blocks.${block}.intro`);
    const blockIntroArr = Array.isArray(blockIntro)
      ? blockIntro
      : typeof blockIntro === 'string'
      ? [blockIntro]
      : [];

    const expandText = t('intro:misc-text.expand');
    const collapseText = t('intro:misc-text.collapse');

    const isBlockCompleted =
      completedCount === challengesWithCompleted.length;

    const percentageCompleted = Math.floor(
      (completedCount / challengesWithCompleted.length) * 100
    );

    const progressBarRender = (
      <div aria-hidden='true' className='progress-wrapper'>
        <div>
          <ProgressBar now={percentageCompleted} />
        </div>
        <span>{`${percentageCompleted}%`}</span>
      </div>
    );

    const courseCompletionStatus = () => {
      if (completedCount === 0) {
        return t('learn.not-started');
      }
      if (completedCount === challengesWithCompleted.length) {
        return t('learn.completed');
      }
      return `${percentageCompleted}% ${t('learn.completed')}`;
    };

    // ---- Grid + Project block (new header design + link to first challenge) ----
    if (isGridBlock && isProjectBlock) {
      const gpBlockClasses = [
        'timeline-block',
        'is-project'
      ].join(' ');

      return (
        <ScrollableAnchor id={block}>
          <div className={gpBlockClasses}>
            <Link
              className='timeline-block-header'
              to={challengesWithCompleted[0].fields.slug}
              style={{ textDecoration: 'none', color: 'inherit' }}
            >
              <div className='timeline-header-left'>
                <span className='timeline-expand-icon'>
                  <ChevronIcon />
                </span>
                <h3 className='timeline-block-title'>{blockTitle}</h3>
              </div>
              <div className='timeline-header-right'>
                <span className='timeline-project-tag'>
                  {t('misc.certification-project')}
                </span>
                {!isAudited && (
                  <span className='timeline-project-tag'>
                    {t('misc.translation-pending')}
                  </span>
                )}
                <span className={`timeline-progress-badge ${isBlockCompleted ? 'is-complete' : ''}`}>
                  {isBlockCompleted && <CheckIcon stroke='#059669' size={12} />}
                  {isBlockCompleted ? t('learn.completed') : t('learn.not-started')}
                </span>
              </div>
            </Link>
          </div>
        </ScrollableAnchor>
      );
    }

    // ---- Grid block (new header design + original grid challenges) ----
    if (isGridBlock) {
      const gridBadgeClass = isBlockCompleted
        ? 'is-complete'
        : completedCount > 0
        ? 'is-in-progress'
        : '';

      const gridBlockClasses = [
        'timeline-block',
        isExpanded ? 'is-expanded' : ''
      ].filter(Boolean).join(' ');

      return (
        <ScrollableAnchor id={block}>
          <div className={gridBlockClasses}>
            <button
              className='timeline-block-header'
              aria-expanded={isExpanded}
              aria-controls={`${block}-panel`}
              onClick={this.handleBlockClick}
            >
              <div className='timeline-header-left'>
                <span className='timeline-expand-icon'>
                  <ChevronIcon />
                </span>
                <h3 className='timeline-block-title'>{blockTitle}</h3>
              </div>
              <div className='timeline-header-right'>
                {!isAudited && (
                  <span className='timeline-project-tag'>
                    {t('misc.translation-pending')}
                  </span>
                )}
                <span className={`timeline-progress-badge ${gridBadgeClass}`}>
                  {isBlockCompleted && <CheckIcon stroke='#059669' size={12} />}
                  {`${completedCount}/${challengesWithCompleted.length}`}
                </span>
              </div>
              {!isExpanded &&
                !isBlockCompleted &&
                completedCount > 0 &&
                progressBarRender}
            </button>
            <div
              className={`timeline-block-body ${isExpanded ? 'is-open' : 'is-closed'}`}
              id={`${block}-panel`}
            >
              <BlockIntros intros={blockIntroArr} />
              <Challenges
                challengesWithCompleted={challengesWithCompleted}
                isProjectBlock={isProjectBlock}
                isGridMap={true}
                blockTitle={blockTitle}
              />
            </div>
          </div>
        </ScrollableAnchor>
      );
    }

    // ---- Project-only block (non-grid) ----
    if (isProjectBlock) {
      const projectBadgeClass = isBlockCompleted
        ? 'is-complete'
        : completedCount > 0
        ? 'is-in-progress'
        : '';

      return (
        <ScrollableAnchor id={block}>
          <div className='timeline-block is-project'>
            <div className='timeline-block-header'>
              <div className='timeline-header-left'>
                <span className='timeline-expand-icon'>
                  <ChevronIcon />
                </span>
                <h3 className='timeline-block-title'>{blockTitle}</h3>
              </div>
              <div className='timeline-header-right'>
                {!isAudited && (
                  <span className='timeline-project-tag'>
                    {t('misc.translation-pending')}
                  </span>
                )}
              </div>
            </div>
            <BlockIntros intros={blockIntroArr} />
            <Challenges
              challengesWithCompleted={challengesWithCompleted}
              isProjectBlock={isProjectBlock}
            />
          </div>
        </ScrollableAnchor>
      );
    }

    // ---- Default block: Timeline rendering for non-grid, non-project blocks ----

    // Determine the first incomplete challenge (the "current" one)
    const firstIncompleteIndex = challengesWithCompleted.findIndex(
      ch => !ch.isCompleted
    );

    // Progress badge state
    const progressLabel = `${completedCount}/${challengesWithCompleted.length}`;
    const badgeClass = isBlockCompleted
      ? 'is-complete'
      : completedCount > 0
      ? 'is-in-progress'
      : '';

    const blockClasses = [
      'block',
      'mb-4',
      'timeline-block',
      isExpanded ? 'is-expanded open' : ''
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <ScrollableAnchor id={block}>
        <div className={blockClasses}>
          {/* ---- Always-visible block title ---- */}
          <div className='block-header'>
            <h3 className='big-block-title'>{blockTitle}</h3>
            {!isAudited && (
              <div className='block-cta-wrapper'>
                <Link
                  className='block-title-translation-cta'
                  to={t('links:help-translate-link-url')}
                >
                  {t('misc.translation-pending')}
                </Link>
              </div>
            )}
          </div>

          {/* ---- Always-visible block description ---- */}
          <BlockIntros intros={blockIntroArr} />

          {/* ---- Collapsible Toggle Button ---- */}
          <button
            aria-expanded={isExpanded}
            aria-controls={`${block}-timeline-panel`}
            className='timeline-collapse-btn'
            onClick={this.handleBlockClick}
          >
            <span className='timeline-collapse-left'>
              <span className='timeline-expand-icon'>
                <ChevronIcon />
              </span>
              <span className='timeline-collapse-text'>
                {isExpanded ? collapseText : expandText}{' '}
                <span className='sr-only'>{blockTitle}</span>
              </span>
            </span>
            <span className='timeline-collapse-right'>
              <span
                className={`timeline-progress-badge ${badgeClass}`}
                aria-hidden='true'
              >
                {isBlockCompleted && <CheckIcon stroke='#059669' size={12} />}
                {progressLabel}
              </span>
              <span className='sr-only'>
                ,{' '}
                {t('learn.challenges-completed', {
                  completedCount,
                  totalChallenges: challengesWithCompleted.length
                })}
              </span>
            </span>
          </button>

          {/* ---- Expanded Timeline Body ---- */}
          <div
            className={`timeline-block-body ${isExpanded ? 'is-open' : 'is-closed'}`}
            id={`${block}-timeline-panel`}
          >
            <ul className='timeline-challenges'>
              {challengesWithCompleted.map((challenge, index) => {
                const isCurrent =
                  !challenge.isCompleted && index === firstIncompleteIndex;
                const isUpcoming =
                  !challenge.isCompleted && index !== firstIncompleteIndex;

                const dotClass = challenge.isCompleted
                  ? 'is-completed'
                  : isCurrent
                  ? 'is-current'
                  : 'is-upcoming';

                const itemClass = challenge.isCompleted
                  ? 'is-completed-item'
                  : isCurrent
                  ? 'is-current-item'
                  : 'is-upcoming-item';

                return (
                  <li
                    key={challenge.id}
                    className={`timeline-item ${itemClass}`}
                    id={challenge.dashedName}
                  >
                    <Link to={challenge.fields.slug}>
                      {/* Status dot */}
                      <span className={`timeline-dot ${dotClass}`}>
                        {challenge.isCompleted && <CheckIcon />}
                      </span>

                      {/* Lesson title */}
                      <span className='timeline-lesson-title'>
                        {challenge.title}
                      </span>

                      {/* Current label */}
                      {isCurrent && (
                        <span className='timeline-current-label'>
                          Current
                        </span>
                      )}

                      {/* Completed check on right */}
                      {challenge.isCompleted && (
                        <span className='timeline-status-icon is-completed'>
                          <CheckIcon stroke='#059669' size={14} />
                        </span>
                      )}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </ScrollableAnchor>
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(withTranslation()(Block));
